<template>
  <template v-if="info.status === 'edit'">
    <a-upload
      :action="uploadPath"
      :file-list="fileList"
      :headers="headers"
      accept="image/*"
      list-type="picture-card"
      @change="onChange"
      @preview="handlePreview"
      :multiple="widget.multi"
    >
      <div
        v-if="info.status === 'edit'"
        style="margin-top: 8px; display: flex; flex-direction: column"
      >
        上传
      </div>
    </a-upload>
    <a-modal
      :footer="null"
      :title="previewTitle"
      :visible="previewVisible"
      @cancel="handleCancel"
    >
      <img :src="previewImage" alt="example" style="width: 100%" />
    </a-modal>
  </template>
  <div v-else>
    <div class="show-box">
      <img
        class="img"
        :src="image"
        :key="index"
        v-for="(image, index) in images"
        @click="() => (visible = !visible)"
      />
      <div style="display: none">
        <a-image-preview-group
          :preview="{ visible, onVisibleChange: (vis) => (visible = vis) }"
        >
          <a-image
            v-for="(image, index) in images"
            :width="50"
            :src="image"
            :key="index"
          />
        </a-image-preview-group>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { definedWidgetMeta } from '@/components/business/crud/widgets/widgetManager';

export const meta = definedWidgetMeta({
  size: { edit: 'normal', show: 'normal' },
});
</script>

<script lang="ts" setup>
import { computed, defineProps, ref } from 'vue';
import { WidgetInfo } from '@/components/business/crud/widgets/WidgetContainer';
import { ScForm } from '@/models/ScForm';
import { UploadChangeParam, UploadProps } from 'ant-design-vue';
import config from '@/config';
import { tokenManager } from '@/util/token';
import { ImgFieldType } from '@/components/business/crud/interface/FieldType';

const props = defineProps<{
  propName: string;
  widget: ImgFieldType;
  info: WidgetInfo;
}>();

const data = ScForm.useFormData();

const uploadPath = config.uploadApiPath;
const uploadList = ref<Exclude<UploadProps['fileList'], undefined>>([]);
const headers = {
  ...tokenManager.get(),
};

function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

const previewVisible = ref(false);
const previewImage = ref('');
const previewTitle = ref('');

const handleCancel = () => {
  previewVisible.value = false;
  previewTitle.value = '';
};
const handlePreview = async (file: any) => {
  if (!file.url && !file.preview) {
    file.preview = (await getBase64(file.originFileObj)) as string;
  }
  previewImage.value = file.url || file.preview;
  previewVisible.value = true;
  previewTitle.value =
    file.name || file.url.substring(file.url.lastIndexOf('/') + 1);
};

const fileList = computed(() => {
  let res: Exclude<UploadProps['fileList'], undefined> = [];
  if (data.value[props.propName]) {
    if (typeof data.value[props.propName] === 'string') {
      res.push({
        uid: data.value[props.propName],
        name: data.value[props.propName],
        status: 'done',
        url: config.uploadPath + data.value[props.propName],
      });
    } else {
      res = data.value[props.propName].map((item: string) => {
        return {
          uid: item,
          name: item,
          status: 'done',
          url: config.uploadPath + item,
        };
      });
    }
  }
  return res.concat(...uploadList.value);
});

function onChange(info: UploadChangeParam) {
  uploadList.value = info.fileList.filter((file) => file.status !== 'done');
  const file = info.file;
  if (file.status === 'removed') {
    if (props.widget.multi) {
      if (data.value[props.propName]) {
        if (!(data.value[props.propName] instanceof Array)) {
          data.value[props.propName] = [data.value[props.propName]];
        }
        data.value[props.propName] = data.value[props.propName].filter(
          (id: string) => id !== file.uid
        );
      }
    } else {
      data.value[props.propName] = null;
    }
  } else if (file.status === 'done') {
    if (props.widget.multi) {
      if (!data.value[props.propName]) {
        data.value[props.propName] = [];
      }
      if (!(data.value[props.propName] instanceof Array)) {
        data.value[props.propName] = [data.value[props.propName]];
      }
      data.value[props.propName].push(file.response.data.path);
    } else {
      data.value[props.propName] = file.response.data.path;
    }
  }
}

const images = computed(() => {
  const images = data.value[props.propName];
  if (images instanceof Array) {
    return images
      .filter((i) => i)
      .map((i) => (i.startsWith('/') ? config.uploadPath + i : i));
  }
  if (typeof images === 'string') {
    return [images.startsWith('/') ? config.uploadPath + images : images];
  }
  return [];
});

const visible = ref(false);
</script>

<style lang="scss" scoped>
.view {
  height: 100%;
  padding-left: 12px;
}

.show-box {
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;

  .img {
    width: 50px;
    height: 50px;
    object-fit: cover;
    margin: 0 5px 5px 0;
    cursor: pointer;
  }
}
</style>
